import React from 'react';
import SafeIcon from '../common/SafeIcon';

export default function Reports() {
  return (
    <div className="space-y-6 max-w-7xl mx-auto flex flex-col h-[calc(100vh-8rem)]">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Report Builder</h1>
          <p className="text-slate-400 text-sm mt-1">Compile, review, and export compliant sustainability reports.</p>
        </div>
        <div className="flex gap-3">
          <button className="px-4 py-2 text-sm font-medium text-slate-300 glass-panel rounded-lg hover:text-white glass-panel-hover flex items-center gap-2">
            <SafeIcon name="Save" className="w-4 h-4" /> Save Draft
          </button>
          <button className="px-4 py-2 text-sm font-medium text-slate-900 bg-cyan-500 rounded-lg hover:bg-cyan-400 transition-colors shadow-[0_0_15px_rgba(6,182,212,0.3)] flex items-center gap-2">
            <SafeIcon name="FileText" className="w-4 h-4" /> Generate PDF
          </button>
        </div>
      </div>

      <div className="flex gap-6 flex-1 min-h-0">
        {/* Sidebar Controls */}
        <div className="w-64 glass-panel rounded-xl p-4 flex flex-col shrink-0 overflow-y-auto">
          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">Frameworks</h3>
          <div className="space-y-2 mb-6">
            {['GHG Protocol', 'TCFD', 'GRI Standards', 'SASB'].map((fw, i) => (
              <label key={i} className="flex items-center gap-3 p-2 rounded hover:bg-slate-800/50 cursor-pointer">
                <input type="checkbox" defaultChecked={i < 2} className="w-4 h-4 rounded border-slate-600 text-emerald-500 focus:ring-emerald-500 bg-slate-900" />
                <span className="text-sm text-slate-300">{fw}</span>
              </label>
            ))}
          </div>

          <h3 className="text-sm font-semibold text-slate-300 uppercase tracking-wider mb-4">Sections</h3>
          <div className="space-y-1">
            {['Executive Summary', 'Methodology', 'Scope 1 & 2 Emissions', 'Scope 3 Value Chain', 'Reduction Targets', 'Assurance Statement'].map((sec, i) => (
              <div key={i} className={`px-3 py-2 text-sm rounded cursor-pointer ${i === 2 ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'}`}>
                {sec}
              </div>
            ))}
          </div>
        </div>

        {/* Document Preview */}
        <div className="flex-1 glass-panel rounded-xl flex flex-col overflow-hidden bg-[#0f172a]/80 relative">
          {/* Editor Toolbar */}
          <div className="h-12 border-b border-slate-800 flex items-center px-4 gap-4 bg-slate-900/50 shrink-0">
            <div className="flex items-center gap-2 border-r border-slate-700 pr-4">
              <SafeIcon name="Bold" className="w-4 h-4 text-slate-400 hover:text-slate-200 cursor-pointer" />
              <SafeIcon name="Italic" className="w-4 h-4 text-slate-400 hover:text-slate-200 cursor-pointer" />
              <SafeIcon name="Underline" className="w-4 h-4 text-slate-400 hover:text-slate-200 cursor-pointer" />
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-400 font-mono">
              <SafeIcon name="AlignLeft" className="w-4 h-4 hover:text-slate-200 cursor-pointer" />
              <SafeIcon name="List" className="w-4 h-4 hover:text-slate-200 cursor-pointer" />
            </div>
            <div className="ml-auto text-xs text-slate-500 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span> Auto-saved
            </div>
          </div>

          {/* Document Content */}
          <div className="flex-1 overflow-y-auto p-8 lg:p-12">
            <div className="max-w-3xl mx-auto bg-slate-50 text-slate-900 p-10 rounded shadow-2xl min-h-full">
              <h1 className="text-3xl font-bold mb-6 font-serif">3.0 Scope 1 & 2 Emissions Analysis</h1>
              <p className="mb-4 text-slate-700 leading-relaxed">
                In accordance with the GHG Protocol Corporate Accounting and Reporting Standard, this section details our direct operations footprint for FY 2024. Total aggregated emissions across Scope 1 and Scope 2 (Location-based) amounted to <strong>12,570.6 tCO2e</strong>.
              </p>
              
              <div className="my-6 p-4 border border-slate-300 rounded bg-slate-100 flex items-start gap-4">
                <SafeIcon name="BarChart2" className="w-6 h-6 text-slate-500 mt-1 shrink-0" />
                <div>
                  <h4 className="font-semibold text-sm mb-1 uppercase tracking-wider text-slate-500">Dynamic Data Source</h4>
                  <p className="text-sm text-slate-600">Chart: [FY24_Scope1_2_Breakdown_v2] will be rendered here upon export.</p>
                </div>
              </div>

              <h2 className="text-xl font-bold mt-8 mb-4 font-serif">3.1 Methodology & Exclusions</h2>
              <p className="mb-4 text-slate-700 leading-relaxed">
                Data was consolidated using the operational control approach. Refrigerant leakages from smaller regional offices (representing &lt;1% of total footprint) were excluded due to data unavailability and deemed immaterial for this reporting cycle.
              </p>
            </div>
          </div>
          
          {/* Audit Notes Panel (Overlay on right side of preview) */}
          <div className="absolute right-4 top-16 w-72 glass-panel border border-amber-500/30 bg-slate-900/95 backdrop-blur-xl rounded-lg shadow-2xl p-4">
            <div className="flex items-center gap-2 text-amber-400 mb-3 border-b border-slate-800 pb-2">
              <SafeIcon name="MessageSquare" className="w-4 h-4" />
              <h4 className="text-sm font-semibold">Audit Notes</h4>
            </div>
            <div className="space-y-3">
              <div className="bg-slate-800/50 p-3 rounded border border-slate-700">
                <div className="flex justify-between items-start mb-1">
                  <span className="text-xs font-semibold text-slate-300">Ext Auditor (PwC)</span>
                  <span className="text-[10px] text-slate-500">2h ago</span>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">Please attach the updated emission factor reference sheet for UK grid electricity used in section 3.0.</p>
                <div className="mt-2 flex gap-2">
                  <button className="text-[10px] bg-slate-700 hover:bg-slate-600 px-2 py-1 rounded text-slate-200">Reply</button>
                  <button className="text-[10px] text-emerald-400 hover:text-emerald-300 px-2 py-1">Resolve</button>
                </div>
              </div>
            </div>
            <div className="mt-3 relative">
              <input type="text" placeholder="Add note..." className="w-full bg-slate-800 border border-slate-700 rounded text-xs p-2 pr-8 text-slate-200 outline-none focus:border-cyan-500" />
              <SafeIcon name="Send" className="w-3 h-3 absolute right-2.5 top-2.5 text-slate-500 cursor-pointer hover:text-cyan-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}